import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';

import useOnBodyClick from '~/hooks/useOnBodyClick';
import useOnKeyPress from '~/hooks/useOnKeyPress';

import { selectContainer, select, inputFocus } from '~/styles/forms';

const Wrapper = styled.div`
    ${selectContainer};

    &::after {
        z-index: 2;
        transform: ${({ isOpen }) =>
            isOpen ? `translateY(-50%) rotate(180deg)` : `translateY(-50%)`};
    }

    &:focus-within {
        &::after {
            transform: ${({ isOpen }) =>
                isOpen
                    ? `translateY(-50%) rotate(180deg)`
                    : `translateY(-50%)`};
        }
    }
`;

const ContentWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    z-index: 1;
`;

const DropdownButton = styled.button`
    ${select};
    text-align: left;
    background-color: ${({ theme }) => theme.colors.primary.white};

    &:focus {
        box-shadow: ${({ isOpen }) =>
            isOpen ? `none` : `0 2px 4px 3px rgba(0, 0, 0, 0.2)`};

        & + ${ContentWrapper} {
            ${({ theme, isOpen }) => isOpen && inputFocus({ theme })};
        }
    }
`;

const DropdownContent = styled.ul`
    ${select};
    height: auto;
    background-color: ${({ theme }) => theme.colors.primary.white};
    display: flex;
    flex-direction: column;
    gap: 12px 0;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        height: auto;
    }
`;

const SelectItem = styled.li``;

const Option = styled.button`
    width: 100%;
    text-align: left;
    color: ${({ theme, selected }) =>
        selected ? theme.colors.text.blue : theme.colors.text.black};
    text-decoration: ${({ selected }) => (selected ? `underline` : `none`)};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.text.blue};
        text-decoration: underline;
    }
`;

const Dropdown = ({ label, options, disabled = false, buttonId = '' }) => {
    const ref = useRef();
    const [isOpen, setIsOpen] = useState(false);

    const handleButtonClick = () => setIsOpen(!isOpen);

    const handleOptionClick = (action) => {
        action();
        setIsOpen(!isOpen);
    };

    useOnKeyPress('Escape', () => {
        if (isOpen) {
            setIsOpen(false);
            ref.current.firstElementChild.focus();
        }
    });

    useOnBodyClick(() => {
        if (isOpen) {
            setIsOpen(!isOpen);
        }
    }, ref);

    return label && options?.length ? (
        <Wrapper {...{ ref, isOpen }}>
            <DropdownButton
                type="button"
                onClick={handleButtonClick}
                id={buttonId}
                aria-expanded={isOpen}
                {...{ disabled, isOpen }}
            >
                {label}
            </DropdownButton>

            <ContentWrapper>
                <AnimateHeight duration={500} height={isOpen ? `auto` : 0}>
                    <DropdownContent>
                        {options.map(({ label, action, selected }) => (
                            <SelectItem key={label}>
                                <Option
                                    type="button"
                                    onClick={() => handleOptionClick(action)}
                                    {...{ selected }}
                                >
                                    {label}
                                </Option>
                            </SelectItem>
                        ))}
                    </DropdownContent>
                </AnimateHeight>
            </ContentWrapper>
        </Wrapper>
    ) : null;
};

Dropdown.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            action: PropTypes.func.isRequired,
            selected: PropTypes.bool
        }).isRequired
    ).isRequired,
    buttonId: PropTypes.string
};

export default Dropdown;
